import _ from "lodash";
/** External */
import React, { useMemo, useState } from "react";
import { Button, Modal, ToggleSwitch } from "@toddle-edu/ds-web";
import classNames from "classnames";

/** Internal */
import FullScreenLoader from "UIComponents/FullScreenLoader";
import { getSnpSetWithCycleStatusQueryVariables } from "InternalTools/modules/InternalToolUtils";
import { getInternalToolGenericDataFromCache } from "InternalTools/modules/InternalToolsGraphqlHelper";
import Loading from "UIComponents/Loading";
import { FormTextInput } from "UIComponents/InputTextField";

/** Relative */
import SetList from "./SetList";
import classes from "./ManageSetsForSnP.scss";

const styles = { color: "red" };

const ManageSetsForSnP = ({
  testId,
  onClose,
  onSubmit,
  organizationId,
  curriculumProgramTypes,
  isQueryLoading,
  selectedProgram,
  handleToggleCurriculumProgramPermissions,
}) => {
  const [sets, setSets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const localTestId = `${testId}-manageSetsForSnP`;

  const snpEnabledCurriculumProgramIds = useMemo(() => {
    const isEnabled = _.get(selectedProgram, "snpEnabled", false);
    return isEnabled ? [_.get(selectedProgram, "id")] : [];
  }, [selectedProgram]);

  const onUpdateSet = setId => {
    const updatedSets = _.map(sets, set => {
      if (set.setId === setId) {
        return {
          ...set,
          setEnabled: !set.setEnabled,
        };
      }
      return set;
    });

    setSets(updatedSets);
  };

  const getChangedSets = (originalSets, shouldGetAddedSets = true) => {
    const originalSetsObj = _.keyBy(originalSets, "setId");

    return _.reduce(
      sets,
      (result, set) => {
        const isSetPermissionChanged =
          _.get(originalSetsObj, `${set.setId}.setEnabled`) !== set.setEnabled;

        if (isSetPermissionChanged && shouldGetAddedSets === set.setEnabled)
          result.push(set.setId);

        return result;
      },
      []
    );
  };

  const onClickSave = async () => {
    const internalToolsGenericData = getInternalToolGenericDataFromCache(
      getSnpSetWithCycleStatusQueryVariables({
        organizationId,
        curriculumUids: curriculumProgramTypes,
      })
    );

    const originalSets = _.get(internalToolsGenericData, "data");
    const addedSets = getChangedSets(originalSets, true);
    const removedSets = getChangedSets(originalSets, false);

    setIsLoading(true);
    try {
      await onSubmit(addedSets, removedSets);
      onClose();
    } catch (e) {
      setIsLoading(false);
    }
  };

  const onToggleCurriculumProgramPermissions = () => {
    handleToggleCurriculumProgramPermissions();

    if (_.get(selectedProgram, "snpEnabled", false)) {
      setSets([]);
    }
  };

  const getCurriculumToggleComponent = () => {
    const labelClasses = classNames(
      classes.curriculumToggleContainer,
      "text-label"
    );

    return _.isEmpty(selectedProgram) ? null : (
      <div className={labelClasses}>
        {_.get(selectedProgram, "label", "")}
        <ToggleSwitch
          onChange={onToggleCurriculumProgramPermissions}
          checked={!_.isEmpty(snpEnabledCurriculumProgramIds)}
        />
      </div>
    );
  };
  const getBodyContent = () => {
    return isQueryLoading ? (
      <Loading />
    ) : _.isEmpty(curriculumProgramTypes) ? null : (
      <SetList
        organizationId={organizationId}
        curriculumProgramTypes={curriculumProgramTypes}
        sets={sets}
        setSets={setSets}
        onUpdateSet={onUpdateSet}
        testId={localTestId}
      />
    );
  };

  return (
    <>
      <Modal width="608px" height="700px" isOpen onClose={onClose}>
        <Modal.Header title="Manage sets" isClosable testId={localTestId} />
        <Modal.Body isScrollable>
          <div className={classes.container}>
            <FormTextInput
              style={styles}
              size="large"
              placeholder="Enter The Organization ID"
              label="Organization ID"
              value={organizationId}
              isRequired
              updateInputField={() => {}}
              disabled={true}
            />
            {getCurriculumToggleComponent()}

            {getBodyContent()}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className={classes.footer}>
            <Button
              variant="neutral"
              type="outlined"
              _legacy_variant="outlined-subtle"
              onClick={onClose}
              testId={`${localTestId}-close`}
            >
              Cancel
            </Button>

            <Button
              variant="progressive"
              onClick={onClickSave}
              testId={`${testId}-save`}
            >
              Save
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
      {isLoading ? <FullScreenLoader /> : null}
    </>
  );
};

export default ManageSetsForSnP;
